import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCurrentUser, updateUserProfile } from '@/api/user';
import type { User } from '@/types/user';
import type { UpdateUserInput } from '@/types/user';
import { useMockWrapper } from '@/mocks/useMockWrapper';
import { createMockUser } from '@/mocks/mockConfig';

/**
 * Real implementation of useCurrentUser without mock support
 */
function useRealCurrentUser() {
  return useQuery<User>({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
}

/**
 * Hook to fetch and cache the current user's profile.
 * Supports ?noguard parameter for development with mock data.
 */
export function useCurrentUser() {
  return useMockWrapper(
    useRealCurrentUser,
    (pathname) => ({
      data: createMockUser(pathname),
      isLoading: false,
      error: null,
      isError: false,
      isSuccess: true,
      status: 'success' as const,
      fetchStatus: 'idle' as const,
      isFetching: false,
      isRefetching: false,
      isPaused: false,
      isLoadingError: false,
      isRefetchError: false,
      dataUpdatedAt: Date.now(),
      errorUpdatedAt: 0,
      failureCount: 0,
      failureReason: null,
      errorUpdateCount: 0,
      isStale: false,
      isPlaceholderData: false,
      isPending: false,
      isFetched: true,
      isFetchedAfterMount: true,
      isInitialLoading: false,
      refetch: () => Promise.resolve({} as ReturnType<typeof useRealCurrentUser>),
      remove: () => {},
    })
  );
}

/**
 * Hook to update the current user's profile and invalidate the cache on success.
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation<User, Error, UpdateUserInput>({
    mutationFn: updateUserProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}
